import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Mail, Search, Download, MessageSquare, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin waitlist dialog — shows everyone who joined the waitlist
 * during BETA_MODE. Supports email search + CSV export so the admin
 * can later notify them en masse when the app opens up.
 */
export const WaitlistDialog = ({ open, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/waitlist`, { withCredentials: true });
      setEntries(res.data?.entries || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar la lista de espera');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const filtered = entries.filter((e) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (e.email || '').toLowerCase().includes(q)
        || (e.name || '').toLowerCase().includes(q)
        || (e.message || '').toLowerCase().includes(q);
  });

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const exportCSV = () => {
    if (!filtered.length) return;
    // CSV with the fields the admin actually needs to send a launch
    // email later (name + email + when they signed up + their message).
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const headers = ['Nombre', 'Email', 'Mensaje', 'Idioma', 'Fecha solicitud'];
    const rows = filtered.map((e) => [
      e.name, e.email, e.message || '', e.locale || 'es', e.created_at || '',
    ].map(escape).join(','));
    const csv = '\uFEFF' + [headers.join(','), ...rows].join('\n');  // BOM for Excel
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `lista-espera-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0" data-testid="waitlist-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b bg-muted/30">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5 text-violet-600" />
            Lista de espera
            <span className="text-xs font-normal text-muted-foreground">
              ({entries.length} {entries.length === 1 ? 'solicitud' : 'solicitudes'})
            </span>
          </DialogTitle>
          <DialogDescription>
            Personas que han pedido aviso cuando la app esté disponible. Cuando Monei dé el OK y desactives BETA_MODE, exporta el CSV y envíales un email para reconvertir su interés.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-3 flex flex-wrap gap-2 items-center border-b">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar por nombre, email o mensaje…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="waitlist-search"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCSV}
            disabled={filtered.length === 0}
            className="h-9 gap-1 text-xs"
            data-testid="waitlist-export-csv"
          >
            <Download className="w-3.5 h-3.5" />
            Exportar CSV ({filtered.length})
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center py-12 text-sm text-muted-foreground italic">
              {entries.length === 0
                ? 'Aún no hay nadie en la lista de espera.'
                : 'Ninguna entrada coincide con la búsqueda.'}
            </p>
          ) : (
            <div className="space-y-2">
              {filtered.map((e) => (
                <div
                  key={e.email}
                  className="p-3 rounded-md border bg-white hover:bg-muted/40 transition-colors"
                  data-testid={`waitlist-entry-${e.email}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{e.name}</p>
                      <a
                        href={`mailto:${e.email}`}
                        className="text-xs text-primary hover:underline break-all"
                      >
                        {e.email}
                      </a>
                      {e.message && (
                        <p className="text-xs text-muted-foreground italic mt-1 flex items-start gap-1">
                          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="break-words">"{e.message}"</span>
                        </p>
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1 flex-shrink-0">
                      <Calendar className="w-3 h-3" />
                      {formatDate(e.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistDialog;
